import api from './api';
import CustomDate from '../utils/CustomDate';
import { baseDate } from '../utils/settings';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Get all story events for a timeline
const getStoryEvents = async (timelineId) => {
  const response = await api.get(`/api/timelines/${timelineId}/storyEvents`);
  
  // Convert date objects to CustomDate objects with proper validation
  const events = response.data.map(event => {
    
    let eventStartDate = null;
    let eventEndDate = null;
    
    if (event.startDate) {
      // Check if startDate has valid year, month, day properties
      const hasValidDateProps = 
        typeof event.startDate.year === 'number' && 
        typeof event.startDate.month === 'number' && 
        typeof event.startDate.day === 'number';
      
      if (hasValidDateProps) {
        eventStartDate = new CustomDate(
          event.startDate.year,
          event.startDate.month,
          event.startDate.day,
          event.startDate.isBC || false
        );
      } else {
        throw new Error(`Invalid start date properties for event "${event.title}": ${JSON.stringify(event.startDate)}`);
      }
    } else {
      throw new Error(`No start date property found for event "${event.title}"`);
    }
    
    // Process endDate if available
    if (event.endDate) {
      const hasValidEndDateProps = 
        typeof event.endDate.year === 'number' && 
        typeof event.endDate.month === 'number' && 
        typeof event.endDate.day === 'number';
      
      if (hasValidEndDateProps) {
        eventEndDate = new CustomDate(
          event.endDate.year,
          event.endDate.month,
          event.endDate.day,
          event.endDate.isBC || false
        );
      }
    }
    
    return {
      ...event,
      startDate: eventStartDate,
      endDate: eventEndDate,
      // For backward compatibility, provide date as an alias to startDate
      date: eventStartDate
    };
  });
  
  return events;
};

// Get a specific story event
const getStoryEventById = async (timelineId, storyEventId) => {
  const response = await api.get(`/api/storyEvents/${storyEventId}`);
  
  // Safely convert date object to CustomDate object
  let eventStartDate = null;
  let eventEndDate = null;
  if (response.data.startDate) {
    if (typeof response.data.startDate.year === 'number' && 
        typeof response.data.startDate.month === 'number' && 
        typeof response.data.startDate.day === 'number') {
      
      eventStartDate = new CustomDate(
        response.data.startDate.year,
        response.data.startDate.month,
        response.data.startDate.day,
        response.data.startDate.isBC || false
      );
    } else {
      // Use fallback date
      eventStartDate = new CustomDate(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        false
      );
    }
  }
  
  // Process endDate if available
  if (response.data.endDate) {
    const hasValidEndDateProps = 
      typeof response.data.endDate.year === 'number' && 
      typeof response.data.endDate.month === 'number' && 
      typeof response.data.endDate.day === 'number';
      
    if (hasValidEndDateProps) {
      eventEndDate = new CustomDate(
        response.data.endDate.year,
        response.data.endDate.month,
        response.data.endDate.day,
        response.data.endDate.isBC || false
      );
    }
  }
  
  const event = {
    ...response.data,
    startDate: eventStartDate,
    endDate: eventEndDate,
    // For backward compatibility, provide date as an alias to startDate
    date: eventStartDate
  };
  return event;
};

// Helper function to process media data before sending to server
const processMediaData = (storyEventData) => {
  // Create a copy to avoid modifying the original
  const processedData = { ...storyEventData };
  
  // Ensure all media arrays exist
  processedData.images = processedData.images || [];
  processedData.videos = processedData.videos || [];
  processedData.mapImages = processedData.mapImages || [];
  
  // Remove temporary videoUrl field if it exists
  if (processedData.videoUrl) {
    delete processedData.videoUrl;
  }
  
  return processedData;
};

// Create a new story event
const createStoryEvent = async (timelineId, storyEventData) => {
  const processedData = processMediaData(storyEventData);
  const response = await api.post(`/api/timelines/${timelineId}/storyEvents`, processedData);
  
  // Safely convert date object to CustomDate object
  let eventStartDate = null;
  let eventEndDate = null;
  if (response.data.startDate) {
    if (typeof response.data.startDate.year === 'number' && 
        typeof response.data.startDate.month === 'number' && 
        typeof response.data.startDate.day === 'number') {
      
      eventStartDate = new CustomDate(
        response.data.startDate.year,
        response.data.startDate.month,
        response.data.startDate.day,
        response.data.startDate.isBC || false
      );
    }
  }
  
  // Process endDate if available
  if (response.data.endDate) {
    const hasValidEndDateProps = 
      typeof response.data.endDate.year === 'number' && 
      typeof response.data.endDate.month === 'number' && 
      typeof response.data.endDate.day === 'number';
      
    if (hasValidEndDateProps) {
      eventEndDate = new CustomDate(
        response.data.endDate.year,
        response.data.endDate.month,
        response.data.endDate.day,
        response.data.endDate.isBC || false
      );
    }
  }
  
  const event = {
    ...response.data,
    startDate: eventStartDate,
    endDate: eventEndDate,
    // For backward compatibility, provide date as an alias to startDate
    date: eventStartDate
  };
  return event;
};

// Update a story event
const updateStoryEvent = async (timelineId, storyEventId, storyEventData) => {
  // Ensure the date is in the correct format for the backend
  const processedData = { ...processMediaData(storyEventData) };
    
  // Check if the date needs formatting
  if (processedData.startDate) {
    // If it's already a simple object with year, month, day as numbers, keep it as is
    const hasCorrectDateFormat = 
      typeof processedData.startDate.year === 'number' &&
      typeof processedData.startDate.month === 'number' &&
      typeof processedData.startDate.day === 'number';
      
    if (!hasCorrectDateFormat) {
      // If it's a CustomDate object with methods
      if (typeof processedData.startDate.getFullYear === 'function') {
        processedData.startDate = {
          year: processedData.startDate.getFullYear(),
          month: processedData.startDate.getMonth(),
          day: processedData.startDate.getDate(),
          isBC: processedData.startDate.isBC || false
        };
      } 
      // If it has a date string property
      else if (processedData.startDate.date) {
        const dateObj = new Date(processedData.startDate.date);
        processedData.startDate = {
          year: dateObj.getFullYear(),
          month: dateObj.getMonth(),
          day: dateObj.getDate(),
          isBC: processedData.startDate.isBC || false
        };
      }
    }
  }

  // Process endDate to handle empty values
  if (processedData.endDate) {
    // Check if endDate has any empty fields or invalid values
    const isEmpty = 
      processedData.endDate.year === '' || 
      processedData.endDate.month === '' || 
      processedData.endDate.day === '' ||
      !processedData.endDate.year ||
      !processedData.endDate.month ||
      !processedData.endDate.day;
    
    if (isEmpty) {
      // Set to null if any fields are empty
      processedData.endDate = null;
    } else {
      // Ensure all values are numbers
      processedData.endDate = {
        year: Number(processedData.endDate.year),
        month: Number(processedData.endDate.month),
        day: Number(processedData.endDate.day),
        isBC: processedData.endDate.isBC || false
      };
    }
  }

  // Process lines to remove startDate and endDate
  if (processedData.lines) {
    processedData.lines = processedData.lines.map(line => ({
      color: line.color || '#000000',
      opacity: line.opacity || 1,
      type: line.type || 'thin'
    }));
  }

  const response = await api.put(`/api/storyEvents/${storyEventId}`, processedData);
  
  // Safely convert date object to CustomDate object
  let eventStartDate = null;
  let eventEndDate = null;
  if (response.data.startDate) {
    if (typeof response.data.startDate.year === 'number' && 
        typeof response.data.startDate.month === 'number' && 
        typeof response.data.startDate.day === 'number') {
      
      eventStartDate = new CustomDate(
        response.data.startDate.year,
        response.data.startDate.month,
        response.data.startDate.day,
        response.data.startDate.isBC || false
      );
    }
  }
  
  // Process endDate if available
  if (response.data.endDate) {
    const hasValidEndDateProps = 
      typeof response.data.endDate.year === 'number' && 
      typeof response.data.endDate.month === 'number' && 
      typeof response.data.endDate.day === 'number';
      
    if (hasValidEndDateProps) {
      eventEndDate = new CustomDate(
        response.data.endDate.year,
        response.data.endDate.month,
        response.data.endDate.day,
        response.data.endDate.isBC || false
      );
    }
  }
  
  const event = {
    ...response.data,
    startDate: eventStartDate,
    endDate: eventEndDate,
    // For backward compatibility, provide date as an alias to startDate
    date: eventStartDate
  };
  
  return event;
};

// Delete a story event
const deleteStoryEvent = async (timelineId, storyEventId) => {
  const response = await api.delete(`/api/storyEvents/${storyEventId}`);
  return response.data;
};

// Bağlantılı olayları getir
export const getLinkedEvents = async (timelineId, storyId) => {
  try {
    const response = await axios.get(`${API_URL}/timelines/${timelineId}/stories/${storyId}/linked-events`);
    return response.data;
  } catch (error) {
    console.error('Bağlantılı olaylar getirilirken hata:', error);
    throw error;
  }
};

// Olay bağlantısı ekle
export const addLinkedEvent = async (timelineId, storyId, linkedEventId) => {
  try {
    console.log('API isteği gönderiliyor:', {
      timelineId,
      storyId,
      linkedEventId
    });
    
    const response = await axios.post(`${API_URL}/timelines/${timelineId}/stories/${storyId}/linked-events`, {
      linkedEventId
    });
    return response.data;
  } catch (error) {
    console.error('Olay bağlantısı eklenirken hata:', error);
    throw error;
  }
};

// Olay bağlantısını kaldır
export const removeLinkedEvent = async (timelineId, storyId, linkedEventId) => {
  try {
    const response = await axios.delete(`${API_URL}/timelines/${timelineId}/stories/${storyId}/linked-events/${linkedEventId}`);
    return response.data;
  } catch (error) {
    console.error('Olay bağlantısı kaldırılırken hata:', error);
    throw error;
  }
};

const storyEventService = {
  getStoryEvents,
  getStoryEventById,
  createStoryEvent,
  updateStoryEvent,
  deleteStoryEvent,
  getLinkedEvents,
  addLinkedEvent,
  removeLinkedEvent,
};

export default storyEventService; 